import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import { FONT } from "@/lib/tokens";
import { appleEase } from "@/lib/motion";

/* ──────────────────────────────────────────────────────────────
   Ce qu'un systeme peut faire.

   Cette section ne presente PAS le catalogue. Elle montre
   l'amplitude de ce qu'un systeme peut etre : d'un agent IA
   multi-outils a une operation metier structuree. Le visiteur doit
   comprendre qu'il n'achete ni un template ni une procedure, mais
   une machine qui produit un resultat.

   Quatre exemples volontairement tres differents, chacun avec son
   flux. Ce sont des ILLUSTRATIONS, pas des produits en vente : la
   section le dit explicitement et aucune carte n'est cliquable
   vers une fiche.

   Sur les marques : elles sont citees comme technologies possibles,
   jamais comme integrations fournies. TANGIG ne vend pas Make, n8n
   ou un modele d'IA — TANGIG distribue le systeme qui les orchestre.
   ────────────────────────────────────────────────────────────── */

const INK    = "#1D1D1F";
const GREY_1 = "#6E6E73";
const GREY_2 = "#86868B";
const LINE   = "#E8E8ED";
const BLUE   = "#1D5FD1";
/* Meme recette que "Comment ca marche" : la carte prend le gris de la
   section et c'est le trait noir qui la dessine. */
const SURFACE = "#F5F5F7";
const BORDER  = "#000000";

const CSS = `
  .tg-ex-section {
    background: #F5F5F7;
    padding: 130px clamp(24px, 5.5vw, 80px) 140px;
    font-family: ${FONT};
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tg-ex-header {
    text-align: center;
    max-width: 780px;
    margin: 0 auto 64px;
  }

  /* Meme token que les autres labels de l'accueil. */
  .tg-ex-eyebrow {
    font-size: clamp(18px, 2vw, 19.5px);
    font-weight: 600;
    letter-spacing: -0.01em;
    color: #3A3A3C;
    margin: 0 0 20px;
  }

  .tg-ex-title {
    font-size: clamp(30px, 4.6vw, 44px);
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.15;
    color: ${INK};
    margin: 0 0 20px;
  }

  .tg-ex-sub {
    font-size: clamp(16px, 1.3vw, 18px);
    line-height: 1.55;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    margin: 0;
  }

  /* ── Quatre cartes, en deux rangees ── */
  .tg-ex-grid {
    width: min(1120px, 100%);
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
    align-items: stretch;
  }

  .tg-ex-card {
    background: #FFFFFF;
    border: 1px solid ${LINE};
    border-radius: 26px;
    padding: 30px 30px 26px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 12px 32px -18px rgba(0,0,0,0.14);
    box-sizing: border-box;
  }

  /* Deux cartes sur quatre sont cernees, en diagonale : elles prennent le
     gris de la section et c'est le trait noir qui les dessine. L'ombre part
     avec le fond blanc, sinon le contour est dit deux fois. */
  .tg-ex-card.is-outlined {
    background: ${SURFACE};
    border-color: ${BORDER};
    box-shadow: none;
  }

  /* Sur ces deux cartes seulement, la fenetre du systeme devient la seule
     surface blanche : sans ca elle se fondrait dans le gris de la carte. */
  .tg-ex-card.is-outlined .tg-ex-screen { background: #FFFFFF; }
  .tg-ex-card.is-outlined .tg-ex-screen-tag { background: ${SURFACE}; }

  .tg-ex-card-top {
    display: flex;
    align-items: baseline;
    gap: 9px;
    margin-bottom: 16px;
  }

  .tg-ex-kind {
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${INK};
  }

  .tg-ex-name {
    font-size: 21px;
    font-weight: 700;
    letter-spacing: -0.025em;
    line-height: 1.22;
    color: ${INK};
    margin: 0 0 10px;
  }

  .tg-ex-result {
    font-size: 14.5px;
    line-height: 1.55;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    margin: 0 0 22px;
  }

  /* ── Le flux : un fragment du systeme en marche ── */
  .tg-ex-screen {
    background: #FAFAFB;
    border: 1px solid ${LINE};
    border-radius: 14px;
    padding: 16px;
    margin-bottom: 20px;
    box-sizing: border-box;
  }

  .tg-ex-screen-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-bottom: 11px;
    margin-bottom: 12px;
    border-bottom: 1px solid ${LINE};
  }

  .tg-ex-screen-title {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${GREY_2};
  }

  .tg-ex-screen-tag {
    font-size: 10px;
    font-weight: 600;
    color: ${GREY_2};
    background: #FFFFFF;
    border: 1px solid ${LINE};
    border-radius: 5px;
    padding: 2px 6px;
    white-space: nowrap;
  }

  .tg-ex-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
  }

  .tg-ex-node {
    width: 100%;
    text-align: center;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${INK};
    background: #FFFFFF;
    border: 1px solid ${LINE};
    border-radius: 8px;
    padding: 7px 9px;
    box-sizing: border-box;
  }
  /* Le noeud bleu est toujours celui ou l'IA travaille : une seule couleur
     pour un seul role, sur les quatre cartes. En noir il se confondait avec
     le reste de la section, entierement en noir et blanc. */
  .tg-ex-node.is-accent {
    background: ${BLUE};
    border-color: ${BLUE};
    color: #FFFFFF;
  }

  .tg-ex-node-tick { color: ${GREY_2}; font-size: 11px; line-height: 1; }

  /* ── Les technologies, en pied de carte ── */
  .tg-ex-tech {
    margin-top: auto;
    border-top: 1px solid ${LINE};
    padding-top: 15px;
    display: flex;
    gap: 10px;
    font-size: 12.5px;
    line-height: 1.45;
    letter-spacing: -0.01em;
  }

  .tg-ex-tech-key { color: ${GREY_2}; flex-shrink: 0; }
  .tg-ex-tech-val { color: ${INK}; }

  /* ── Le mot sous la galerie ── */
  .tg-ex-note { width: min(1120px, 100%); margin: 44px auto 0; text-align: center; }

  .tg-ex-note-text {
    font-size: 15.5px;
    font-weight: 500;
    line-height: 1.6;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    max-width: 660px;
    margin: 0 auto 24px;
  }

  .tg-ex-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 28px;
    background: transparent;
    color: ${BLUE};
    border: 1px solid ${BLUE};
    border-radius: 99px;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    text-decoration: none;
    transition: all 0.3s ease;
  }
  .tg-ex-cta:hover { background: #F0F5FE; }

  /* Hors mobile la galerie est une grille : aucune puce a afficher. */
  .tg-ex-dots { display: none; }

  /* ── Mobile : la galerie defile lateralement ──────────────────
     La grille devient une bande scrollable. Le padding lateral
     passe de la section aux blocs, pour que la bande touche les
     bords de l'ecran tout en gardant 22 px de marge avant la
     premiere carte — une carte collee au bord n'est jamais belle.
     Le spacer final garantit la meme marge a droite : le
     padding-right d'un conteneur scrollable est ignore par
     certains navigateurs. */
  @media (max-width: 900px) {
    .tg-ex-section { padding: 88px 0 96px; }
    .tg-ex-header { padding: 0 22px; margin-bottom: 28px; }

    .tg-ex-grid {
      display: flex;
      width: 100%;
      gap: 14px;
      padding: 6px 22px 10px;
      box-sizing: border-box;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x mandatory;
      scroll-padding-left: 22px;
      scrollbar-width: none;
    }
    .tg-ex-grid::-webkit-scrollbar { display: none; }

    /* Cartes larges : le texte tient sur moins de lignes, donc la carte
       est nettement moins haute. Le bord de la suivante reste visible. */
    .tg-ex-card {
      flex: 0 0 90%;
      scroll-snap-align: start;
      padding: 26px 22px 22px;
      border-radius: 22px;
    }

    .tg-ex-result { margin-bottom: 18px; }
    .tg-ex-screen { padding: 14px; margin-bottom: 18px; }
    .tg-ex-node { padding: 6px 8px; }

    /* ── Les puces : elles disent qu'il y a autre chose a droite ── */
    .tg-ex-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin: 0 0 16px;
    }

    .tg-ex-dot {
      width: 7px;
      height: 7px;
      padding: 0;
      border: none;
      border-radius: 999px;
      background: #C7C7CC;
      cursor: pointer;
      transition: background 0.25s ease, width 0.25s ease;
      -webkit-appearance: none;
      appearance: none;
    }
    .tg-ex-dot.is-active { width: 22px; background: ${INK}; }

    .tg-ex-grid::after {
      content: "";
      flex: 0 0 8px;
    }

    .tg-ex-name { font-size: 20px; }
    .tg-ex-note { padding: 0 22px; margin-top: 32px; }
  }
`;

/* ──────────────────────────────────────────────────────────────
   Quatre familles volontairement eloignees les unes des autres :
   un agent autonome, une automatisation metier, une chaine de
   production, un bot de veille. L'amplitude est le message.
   ────────────────────────────────────────────────────────────── */
const EXAMPLES = [
  {
    kind: "Agent IA",
    name: "Traiter les factures entrantes sans aucune saisie",
    result:
      "L'agent lit chaque pièce jointe reçue, en extrait les montants, la TVA et les dates, repère les doublons et les anomalies, puis écrit la dépense dans la comptabilité.",
    screenTag: "Agent autonome",
    outlined: true,
    flow: [
      { label: "Facture reçue par e-mail" },
      { label: "Lecture et extraction par l'IA", accent: true },
      { label: "Contrôle des anomalies" },
      { label: "Écriture en comptabilité" },
      { label: "Alerte en cas de doute" },
    ],
    tech: "Automatisation · Modèle IA multimodal · API comptable",
  },
  {
    kind: "Automatisation",
    name: "Ne plus laisser une demande commerciale sans réponse",
    result:
      "Chaque demande entrante est qualifiée selon vos propres critères, consignée, et déclenche la réponse adaptée puis la relance si le silence dure.",
    screenTag: "Temps réel",
    flow: [
      { label: "Formulaire ou e-mail" },
      { label: "Qualification par l'IA", accent: true },
      { label: "Enregistrement au registre" },
      { label: "Réponse envoyée" },
      { label: "Relance automatique" },
    ],
    tech: "Make ou n8n · Modèle IA · CRM ou tableur · Messagerie",
  },
  {
    kind: "Chaîne de production",
    name: "Transformer une réunion en plusieurs contenus publiés",
    result:
      "Un enregistrement déposé est transcrit, puis trois agents spécialisés en tirent chacun un format différent — et la publication part au bon moment.",
    screenTag: "Multi-agents",
    flow: [
      { label: "Audio ou vidéo déposé" },
      { label: "Transcription" },
      { label: "Trois agents rédigent", accent: true },
      { label: "Validation humaine" },
      { label: "Publication programmée" },
    ],
    tech: "Automatisation · Transcription · Modèles IA · Outil de publication",
  },
  {
    kind: "Veille",
    name: "Surveiller son marché sans jamais y penser",
    result:
      "Le système visite chaque jour les sources que vous avez choisies, détecte ce qui a changé, et vous remet un briefing lisible le lundi matin.",
    screenTag: "Quotidien",
    outlined: true,
    flow: [
      { label: "Sources suivies" },
      { label: "Collecte quotidienne" },
      { label: "Analyse des écarts par l'IA", accent: true },
      { label: "Historique conservé" },
      { label: "Briefing du lundi" },
    ],
    tech: "Collecte web ou API · Modèle IA · Base de données · E-mail",
  },
];

export default function SystemExamplesSection() {
  /* La bande n'existe qu'en mobile ; sur desktop c'est une grille et les
     puces sont masquees en CSS, donc ce code ne coute rien la-bas. */
  const gridRef = useRef(null);
  const [active, setActive] = useState(0);

  const syncActive = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    /* On prend la carte dont le bord gauche est le plus proche de celui
       de la bande : c'est celle sur laquelle le snap vient de caler. */
    const left = grid.scrollLeft;
    let best = 0;
    let bestGap = Infinity;
    Array.from(grid.children).forEach((child, i) => {
      if (!(child instanceof HTMLElement)) return;
      const gap = Math.abs(child.offsetLeft - grid.offsetLeft - left);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    setActive(best);
  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    grid.addEventListener("scroll", syncActive, { passive: true });
    return () => grid.removeEventListener("scroll", syncActive);
  }, [syncActive]);

  const goTo = (i) => {
    const grid = gridRef.current;
    const card = grid?.children?.[i];
    if (!grid || !card) return;
    grid.scrollTo({ left: card.offsetLeft - grid.offsetLeft, behavior: "smooth" });
  };

  const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.8, ease: appleEase },
  };

  return (
    <MotionConfig reducedMotion="user">
      <style>{CSS}</style>
      <section className="tg-ex-section">
        <div className="tg-ex-header">
          <motion.p className="tg-ex-eyebrow" {...reveal}>
            Exemples
          </motion.p>
          <motion.h2
            className="tg-ex-title"
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.08 }}
          >
            Ce qu&apos;un système peut faire.
          </motion.h2>
          <motion.p
            className="tg-ex-sub"
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.16 }}
          >
            Un système peut automatiser une tâche, orchestrer plusieurs outils, faire
            travailler un agent IA ou structurer une opération entière.
          </motion.p>
        </div>

        <div className="tg-ex-dots" role="tablist" aria-label="Exemples de systèmes">
          {EXAMPLES.map((ex, i) => (
            <button
              key={ex.kind}
              type="button"
              role="tab"
              className={`tg-ex-dot${i === active ? " is-active" : ""}`}
              aria-selected={i === active}
              aria-label={ex.kind}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        <div className="tg-ex-grid" ref={gridRef}>
          {EXAMPLES.map((ex, i) => (
            <motion.article
              key={ex.kind}
              className={`tg-ex-card${ex.outlined ? " is-outlined" : ""}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: appleEase, delay: 0.08 + (i % 2) * 0.1 }}
            >
              <div className="tg-ex-card-top">
                <span className="tg-ex-kind">{ex.kind}</span>
              </div>

              <h3 className="tg-ex-name">{ex.name}</h3>
              <p className="tg-ex-result">{ex.result}</p>

              <div className="tg-ex-screen">
                <div className="tg-ex-screen-head">
                  <span className="tg-ex-screen-title">Le flux</span>
                  <span className="tg-ex-screen-tag">{ex.screenTag}</span>
                </div>
                <div className="tg-ex-flow">
                  {ex.flow.map((n, s) => (
                    <React.Fragment key={n.label}>
                      <span className={`tg-ex-node${n.accent ? " is-accent" : ""}`}>
                        {n.label}
                      </span>
                      {s < ex.flow.length - 1 && (
                        <span className="tg-ex-node-tick" aria-hidden="true">
                          ↓
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="tg-ex-tech">
                <span className="tg-ex-tech-key">Technologies</span>
                <span className="tg-ex-tech-val">{ex.tech}</span>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div className="tg-ex-note" {...reveal}>
          <p className="tg-ex-note-text">
            Ces exemples ne sont pas des produits en vente : ils montrent les types
            de systèmes qu&apos;on peut construire, transmettre et installer. TANGIG ne
            vend ni Make, ni n8n, ni un modèle d&apos;IA — TANGIG distribue le système qui
            les orchestre.
          </p>
          <Link className="tg-ex-cta" to="/marketplace">
            Voir les systèmes disponibles
          </Link>
        </motion.div>
      </section>
    </MotionConfig>
  );
}
